import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FiInbox } from 'react-icons/fi'
import { FaFilter } from 'react-icons/fa'
import { VscClearAll } from 'react-icons/vsc'

import Input from "../../shared/components/form/input"
import useMyPermission from '../../hooks/useMyPermission'
import HistoricFilterModal from './HistoricFilterModal'
import './ListHistoric.css'

const ACTION_LABEL = {
    create: { label: 'Criou', className: 'hist-badge-create' },
    update: { label: 'Editou', className: 'hist-badge-update' },
    delete: { label: 'Apagou', className: 'hist-badge-delete' },
}

const EMPTY_FILTERS = { action: 'all', entity: null, dateFrom: '', dateTo: '' }

function formatDateTime(value) {
    if (!value) return ''
    return new Date(value).toLocaleString('pt-BR')
}

function ListHistoric() {
    const [historics, setHistorics] = useState([])
    const [myPermission, setMyPermission] = useState({ group: "" })
    const [token] = useState(localStorage.getItem('token') || '')
    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [modalFilter, setModalFilter] = useState(false)
    const [load, setLoad] = useState(false)

    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, "historic_view")

    useEffect(() => {
        api.get('/historic/getall', {
            headers: {
                Authorization: `Bearer ${JSON.parse(token)}`
            }
        }).then((response) => {
            setMyPermission(response.data.permission)
            setHistorics(response.data.historics || [])
            setLoad(true)
        })
    }, [token])

    const sortedHistorics = useMemo(
        () => [...historics].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
        [historics]
    )

    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        return sortedHistorics.filter((item) => {
            const matchSearch =
                (item.name || '').toLowerCase().includes(searchLowerCase) ||
                (item.text || '').toLowerCase().includes(searchLowerCase)
            const matchAction = filters.action === 'all' || item.action === filters.action
            const matchEntity = !filters.entity || item.entity === filters.entity.value
            const itemDate = item.date ? item.date.slice(0, 10) : ''
            const matchDateFrom = !filters.dateFrom || (itemDate && itemDate >= filters.dateFrom)
            const matchDateTo = !filters.dateTo || (itemDate && itemDate <= filters.dateTo)
            return matchSearch && matchAction && matchEntity && matchDateFrom && matchDateTo
        })
    }, [sortedHistorics, search, filters])

    const hasActiveFilter = !!search
        || filters.action !== 'all'
        || !!filters.entity
        || !!filters.dateFrom
        || !!filters.dateTo

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando histórico...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Histórico do Sistema</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions" />
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar por usuário ou ação..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'registro' : 'registros'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'registro encontrado' : 'registros encontrados'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <div>
                        {filter.map((item) => (
                            <div key={item._id} className="pg-item hist-item">
                                <div className="pg-item-main">
                                    <div className="pg-item-header">
                                        <span className="pg-item-name">{item.name}</span>
                                        {item.action && (
                                            <span className={`hist-badge ${ACTION_LABEL[item.action]?.className || ''}`}>
                                                {ACTION_LABEL[item.action]?.label || item.action}
                                            </span>
                                        )}
                                    </div>
                                    <div className="hist-item-text">{item.text}</div>
                                </div>
                                <div className="hist-item-date">{formatDateTime(item.date)}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum registro encontrado</p>
                        <p className="pg-empty-subtitle">
                            {historics.length === 0
                                ? 'As ações realizadas no sistema aparecerão aqui.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            {modalFilter && (
                <HistoricFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                    historics={historics}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o histórico</p>
        </div>
    )
}

export default ListHistoric
